import { Statement2 } from '@efex-services/client/types';
import { FormControl, Grid, Link, TextField, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { icon_check_circle_green } from '@/assets';
import CompanySectorAutocomplete from './CompanySectorAutocomplete';
import PostalCodeAutocomplete from './PostalCodeAutocomplete';
import { addressMaxLength } from '../../constants';

export type FormData = {
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postCode?: number;
  taxId: string;
  incorporation?: DateTime;
  constitutionFile?: File | undefined;
  certificateFile?: File | undefined;
  satFile?: File | undefined;
  neighborhood?: string;
  businessSectorId?: number;
  legalRepresentativeStatament?: boolean;
  externalNumber?: string;
  internalNumber?: string;
};

interface AddressMxFormProps {
  register: UseFormRegister<FormData>;
  FieldsWatched: any;
  errors: any;
  control: Control<FormData, any>;
  setValue: UseFormSetValue<FormData>;
  data: any;
  isPersonForm?: boolean;
  showCompanySectorError?: boolean;
}

const AddressMxForm = ({
  register,
  FieldsWatched,
  setValue,
  errors,
  data,
  showCompanySectorError = false,
}: AddressMxFormProps) => {
  const { t } = useTranslation();
  const [postalCodeData, setPostalCodeData] = useState<IPostalCode | null>();
  const [businessSectorId, setBusinessSectorId] =
    useState<ICompanySector | null>();
  const handleSelectPostalCode = (postalCode?: IPostalCode) => {
    setPostalCodeData(postalCode);
  };

  useEffect(() => {
    if (postalCodeData) {
      setValue('neighborhood', postalCodeData.neighborhood.description);
      setValue('city', postalCodeData.municipality.description);
      setValue('state', postalCodeData.federalEntity.description);
      setValue('postCode', postalCodeData.id);
    }
  }, [postalCodeData, setValue]);

  useEffect(() => {
    if (businessSectorId) {
      setValue('businessSectorId', businessSectorId.id);
    }
  }, [businessSectorId, setValue]);

  useEffect(() => {
    if (data && data.postData && data.postData.postalCode) {
      setPostalCodeData(data.postData);
    }
    if (data && data.companySector && data.companySector.economicActivityKey) {
      setBusinessSectorId(data.companySector);
    }
    if (data && data.statements && data.statements.length > 0) {
      setValue(
        'legalRepresentativeStatament',
        data.statements.find(
          (statement: Statement2) => statement.statement.id === 2
        ).value
      );
    }
  }, [data, setValue]);

  return (
    <>
      <Grid container className="!my-[15px]">
        <Grid item xs={12} md={12}>
          <FormControl className="!mt-3" fullWidth>
            <Typography variant="subtitle2">{t('regMxSector')}</Typography>
            <div className="flex w-full">
              <CompanySectorAutocomplete
                className="custom-textfield w-[90%]"
                data={data}
                selectedValue={businessSectorId}
                showCompanySectorError={showCompanySectorError}
                onSelectCompanySector={setBusinessSectorId}
              />
              {postalCodeData?.id && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </div>
          </FormControl>
        </Grid>

        <Grid item xs={12} className="!my-[15px]">
          <Typography className="!mb-4" variant="h6">
            {t('regMxTitle')}
          </Typography>

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {t(`onbCompanyAddressUSA`)}
            </Typography>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                error={!!errors?.addressLine1}
                placeholder={t(`mxAddressLine1`) as string}
                inputProps={{
                  pattern: '[a-zA-Z0-9\\s]+',
                  onInput: (e: React.FormEvent<HTMLInputElement>) => {
                    const input = e.currentTarget;
                    input.value = input.value.replace(/[^a-zA-Z0-9\s]/g, '');
                  },
                }}
                {...register('addressLine1', {
                  required: true,
                  maxLength: addressMaxLength,
                  pattern: {
                    value: /^[a-zA-Z0-9\s]+$/,
                    message: t('addressInvalidCharacters'),
                  },
                })}
              />
              {FieldsWatched[1] && FieldsWatched[1].length > 0 && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </div>
            {errors?.addressLine1 &&
              errors?.addressLine1.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            {errors?.addressLine1 &&
              errors?.addressLine1.type === 'maxLength' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('MaxLengthCharacters', { max: addressMaxLength })}
                </p>
              )}
          </FormControl>
        </Grid>

        <Grid container className="!w-[90%]">
          <Grid item xs={12} md={6} className="!my-[15px]">
            <FormControl fullWidth>
              <Typography variant="subtitle2">{t(`externalNumber`)}</Typography>
              <div className="flex w-full">
                <TextField
                  fullWidth
                  autoComplete="off"
                  type="text"
                  size="small"
                  error={!!errors?.externalNumber}
                  placeholder="3A"
                  {...register('externalNumber', {
                    required: true,
                    maxLength: addressMaxLength,
                  })}
                />
              </div>
              {errors?.externalNumber &&
                errors?.externalNumber.type === 'required' && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              {errors?.externalNumber &&
                errors?.externalNumber.type === 'maxLength' && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('MaxLengthCharacters', { max: addressMaxLength })}
                  </p>
                )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} className="!my-[15px] !pl-4">
            <FormControl fullWidth>
              <Typography variant="subtitle2">{t(`internalNumber`)}</Typography>
              <div className="flex w-full">
                <TextField
                  fullWidth
                  autoComplete="off"
                  type="text"
                  size="small"
                  error={!!errors?.internalNumber}
                  placeholder="Depto 1"
                  {...register('internalNumber', {
                    maxLength: addressMaxLength,
                  })}
                />
              </div>
              {errors?.internalNumber &&
                errors?.internalNumber.type === 'required' && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              {errors?.internalNumber &&
                errors?.internalNumber.type === 'maxLength' && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('MaxLengthCharacters', { max: addressMaxLength })}
                  </p>
                )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid item xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {t('regMxPost')}{' '}
                <Link
                  href="https://www.correosdemexico.gob.mx/SSLServicios/ConsultaCP/Descarga.aspx"
                  target="_blank"
                >
                  {t('regMxPostLink')}
                </Link>
              </Typography>
              <div className="flex w-full">
                <PostalCodeAutocomplete
                  className="custom-textfield w-[90%]"
                  onSelectPostalCode={handleSelectPostalCode}
                  defaultPostCode={postalCodeData?.postalCode}
                />
                {postalCodeData?.id && (
                  <img
                    src={icon_check_circle_green}
                    alt=""
                    className="w-[25px] ml-[10px]"
                  />
                )}
              </div>
            </FormControl>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">{t('regMxColony')}</Typography>
          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                error={!!errors.neighborhood}
                className="custom-textfield w-[90%]"
                size="small"
                disabled
                placeholder="Colonia"
                {...register('neighborhood', {
                  required: true,
                })}
              />
              {FieldsWatched[3] && FieldsWatched[3].length > 0 && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </div>
            {errors && errors.city && errors.city.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.city && errors.city.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
          </FormControl>
        </Grid>
      </Grid>
      <Grid container className="!my-[15px] !w-[90%]">
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">{t(`city`)}</Typography>
          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                disabled
                fullWidth
                className="custom-textfield"
                size="small"
                placeholder={t(`city`) as string}
                {...register('city', {
                  required: true,
                })}
              />
              {FieldsWatched[3] && FieldsWatched[3].length > 0 && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </div>
            {errors && errors.city && errors.city.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.city && errors.city.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} className="!pl-4">
          <Typography variant="subtitle2">{t(`state`)}</Typography>
          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                disabled
                fullWidth
                className="custom-textfield"
                size="small"
                placeholder={t(`state`) as string}
                aria-readonly
                {...register('state', {
                  required: true,
                })}
              />
              {FieldsWatched[4] && FieldsWatched[4].length > 0 && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </div>
            {errors && errors.state && errors.state.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

export default AddressMxForm;
